import React from 'react';
import styled from 'styled-components';
import { useMutation } from '@apollo/client';
import { black, gray } from '@avangard/ui/colors';

import { GetChatsQuery, MarkViewedChatMessagesMutation } from '@modules/chat/graphql';
import { ChatType } from '@modules/types/graphql';
import { ChatsMessageItem } from './chats-messages-item';

import type { ChatMessageGroupByDate } from '@modules/chat/entities';
import type {
    MarkViewedChatMessagesMutationType,
    MarkViewedChatMessagesMutationVariables,
} from '@modules/types/graphql';

type ChatsMessagesProps = {
    groupsMessages: ChatMessageGroupByDate[];
};

const ChatsMessages = (props: ChatsMessagesProps) => {
    const { groupsMessages } = props;

    const [markViewedChatMessages] = useMutation<
        MarkViewedChatMessagesMutationType,
        MarkViewedChatMessagesMutationVariables
    >(MarkViewedChatMessagesMutation);

    React.useEffect(() => {
        const newMessagesIds: string[] = [];

        groupsMessages.forEach(group => {
            if (group.hasNewMessages) {
                group.messages.forEach(message => {
                    if (!message.viewed) {
                        newMessagesIds.push(message.id);
                    }
                });
            }
        });

        if (newMessagesIds.length > 0) {
            markViewedChatMessages({
                variables: { ids: newMessagesIds },
                refetchQueries: [
                    {
                        query: GetChatsQuery,
                        variables: {
                            type: ChatType.soa,
                        },
                    },
                ],
                awaitRefetchQueries: true,
            });
        }
    }, [groupsMessages]);

    return (
        <Root>
            {groupsMessages.map(group => {
                return (
                    <Group key={group.day}>
                        <h3>{group.day}</h3>

                        {group.messages.map(message => (
                            <ChatsMessageItem key={message.id} message={message} />
                        ))}
                    </Group>
                );
            })}
        </Root>
    );
};

const Root = styled.div`
    padding: 24px;
    overflow: auto;

    & > *:not(:last-child) {
        padding-bottom: 24px;
    }
`;

const Group = styled.div`
    h3 {
        position: relative;
        margin-bottom: 24px;

        color: ${black[54]};
        font-size: 1.2rem;
        font-weight: 500;

        text-align: center;

        &:before,
        &:after {
            content: '';
            position: absolute;
            top: 50%;
            width: 35%;
            height: 1px;
            background-color: ${gray[30]};
        }

        &:before {
            left: 0;
        }

        &:after {
            right: 0;
        }
    }

    & > *:not(:first-child, :last-child) {
        margin-bottom: 12px;
    }
`;

export { ChatsMessages };
